/**
 * Cierre.tsx — los pasos del cierre mensual, guiados.
 *
 * E7. El cierre existía en la base desde E3 y **no se podía alcanzar desde
 * ningún lado**: ni endpoint, ni pantalla, ni comando. Un mes solo se podía
 * cerrar desde una suite de pruebas.
 *
 * ── ▶ ESTA PANTALLA NO DECIDE NADA ────────────────────────────────────────
 *
 * Ni qué paso bloquea, ni cuál se puede firmar, ni si el mes se puede cerrar.
 * Todo eso llega de `control.pasos_del_cierre`. El botón de firmar aparece
 * donde `comprobable` es falso porque **la base lo dice**, no porque acá haya
 * una lista de cuáles son — una segunda lista discreparía el día que la
 * primera cambie, y esta pantalla ofrecería firmar algo que la base rechaza.
 *
 * ── ▶ GUIADA, NO UNA TABLA DE AUDITORÍA ───────────────────────────────────
 *
 * La primera versión era una tabla de once filas con todos los pasos en el
 * mismo plano, el que hay que correr al lado del que no aplica. Una persona que
 * cierra el mes no quiere auditar once filas: quiere saber QUÉ le falta y en
 * qué orden. Así que se parten en dos: **lo que requiere una acción** —arriba,
 * cada uno su tarjeta con su botón— y **lo que ya está resuelto o no aplica**
 * —abajo, en una lista corta que no estorba—. Lo que se asienta al cerrar
 * (congelar índices, bloquear) va con lo resuelto: no es una acción aparte.
 *
 * ── Firmar no es comprobar, y se dibuja distinto ──────────────────────────
 *
 * «Comprobado» y «Firmado» son dos estados y no dos palabras para lo mismo: en
 * el segundo consta quién lo dijo, y eso es todo lo que hay. Enseñarlos con el
 * mismo color convertiría la firma en una comprobación de segunda.
 */
import { useCallback, useEffect, useState } from 'react';
import { useIdioma } from '../nucleo/idioma.tsx';
import { hoy } from '../nucleo/hoy.ts';
import { puedeAdministrar, puedeRegistrar, useMesDeTrabajo, useSesion } from '../nucleo/sesion.tsx';
import { descargarArchivo } from '../nucleo/descargar.ts';
import { esCero, sumar } from '../nucleo/dinero.ts';
import {
  abrirEjercicio, afirmarPaso, cerrarEjercicio, cerrarPeriodo,
  descargarEstado, reabrirPeriodo,
  provisionar, reconocerAvance, revaluarPeriodo, traerEjercicios,
  traerPasosDelCierre, traerProyectos, FalloDeApi,
  type AvancePrevisto, type Ejercicio, type PasoDelCierre,
} from '../datos/libros.ts';

const MESES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] as const;

/**
 * Lo que se lleva quien acaba de cerrar un mes.
 *
 * ▶ `presentacion` NO está, y no es un olvido: necesita decir A QUÉ MONEDA, y
 * esa elección vive en la pantalla de reportes, que tiene el conmutador. Un
 * botón acá tendría que suponerla, y un estado financiero convertido a una
 * moneda que nadie eligió sale con la misma cara que uno correcto.
 */
const EXPORTABLES = [
  { tipo: 'comprobacion', clave: 'rep.tab.trial' },
  { tipo: 'resultado', clave: 'rep.tab.income' },
  { tipo: 'balance', clave: 'rep.tab.balance' },
  { tipo: 'd100', clave: 'rep.tab.d100' },
  { tipo: 'indices', clave: 'idx.title' },
] as const;

export function Cierre() {
  const { t, monto, fecha: comoFecha } = useIdioma();
  const { pedir, sesion } = useSesion();
  const moneda = sesion?.empresa.monedaLocal ?? '';
  const escribe = puedeRegistrar(sesion);
  /* ▶ Abrir un ejercicio es de ADMIN y firmar los pasos del cierre no. Son dos
     permisos distintos y esta pantalla usaba uno solo — ver `puedeAdministrar`. */
  const administra = puedeAdministrar(sesion);

  const enCurso = useMesDeTrabajo();
  const [anio, setAnio] = useState(enCurso.anio);
  const [mes, setMes] = useState(enCurso.mes);

  const [pasos, setPasos] = useState<readonly PasoDelCierre[] | null>(null);
  const [fallo, setFallo] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [nota, setNota] = useState<Record<number, string>>({});
  const [bajando, setBajando] = useState<string | null>(null);
  const [ejercicios, setEjercicios] = useState<readonly Ejercicio[] | null>(null);
  const [avances, setAvances] = useState<readonly AvancePrevisto[] | null>(null);

  const cargar = useCallback(async () => {
    setFallo(null);
    try {
      setPasos(await traerPasosDelCierre(pedir, anio, mes));
      setEjercicios(await traerEjercicios(pedir));
    } catch (e) {
      setPasos(null);
      setEjercicios(null);
      const f = e instanceof FalloDeApi ? e : null;
      setFallo(f?.clase === 'conexion' ? 'sb.conexion'
        : f?.codigo ? `sb.${f.codigo}` : 'sb.unknown');
      return;
    }
    /* ▶ El avance por proyecto, para enseñar en la tarjeta de reconocimiento QUÉ
       se va a asentar sin tener que ir a Proyectos. Va en su propio `try`: es un
       enriquecimiento, no el cierre. Si falla, la tarjeta cae a sólo el botón —
       nunca se lleva por delante la pantalla entera. */
    try {
      const r = await traerProyectos(pedir, anio, mes);
      setAvances(r.previo);
    } catch {
      setAvances(null);
    }
  }, [pedir, anio, mes]);

  useEffect(() => { void cargar(); }, [cargar]);

  async function correr(accion: () => Promise<void>) {
    setOcupado(true);
    setFallo(null);
    setAviso(null);
    try {
      await accion();
      await cargar();
    } catch (e) {
      const f = e instanceof FalloDeApi ? e : null;
      setFallo(f?.clase === 'conexion' ? 'sb.conexion'
        : f?.codigo ? `sb.${f.codigo}` : 'sb.unknown');
    } finally {
      setOcupado(false);
    }
  }

  /**
   * ▶ EXPORTAR ES LEER, así que esto NO está detrás de `escribe`.
   *
   * Una empresa suspendida cae al piso: lee y exporta, no registra. Si estos
   * botones colgaran de la capacidad de escritura, el piso perdería justo lo
   * que promete — y es el defecto que Backlog ya pagó (cambio 52 del hub). La
   * API tampoco lo comprueba, por la misma razón.
   */
  async function exportar(tipo: string) {
    setBajando(tipo);
    setFallo(null);
    try {
      const { nombre, blob } = await descargarEstado(pedir, tipo, anio, mes);
      descargarArchivo(nombre, blob);
    } catch (e) {
      const f = e instanceof FalloDeApi ? e : null;
      setFallo(f?.clase === 'conexion' ? 'sb.conexion'
        : f?.codigo ? `sb.${f.codigo}` : 'sb.unknown');
    } finally {
      setBajando(null);
    }
  }

  // El periodo está cerrado cuando el paso 10 lo está. Sale del mismo sitio que
  // todo lo demás: preguntarlo aparte sería una segunda fuente para el mismo
  // hecho, y las dos podrían no coincidir en la misma pantalla.
  const cerrado = pasos?.find((p) => p.paso === 10)?.estado === 'cumplido';
  const bloqueantes = (pasos ?? []).filter(
    (p) => p.bloquea && p.estado !== 'cumplido' && p.estado !== 'afirmado'
      && p.estado !== 'no_aplica');

  // ▶ Los dos grupos. «al_cerrar» es un pendiente que no se acciona —pasa en la
  // transacción del cierre—, así que va con lo resuelto y no con lo que pide
  // una acción. Todo lo demás pendiente, arriba.
  const porHacer = (pasos ?? []).filter(
    (p) => p.estado === 'pendiente' && p.motivo !== 'al_cerrar');
  const resueltos = (pasos ?? []).filter(
    (p) => !(p.estado === 'pendiente' && p.motivo !== 'al_cerrar'));
  const totalBloquea = (pasos ?? []).filter((p) => p.bloquea).length;
  const progresoPct = totalBloquea === 0 ? 100 : Math.round(((totalBloquea - bloqueantes.length) / totalBloquea) * 100); // regla-ok: son conteos de pasos del cierre, no dinero
  const hayAvance = porHacer.some(
    (p) => p.paso === 5 && p.motivo === 'falta_reconocer_avance');
  // ▶ El avance pendiente de reconocer, por proyecto, para la tarjeta del paso 5.
  // Nada de aritmética a mano: `esCero` y `sumar` viven en dinero.ts (guardián 3).
  const avancePendiente = (avances ?? []).filter(
    (a) => !esCero({ valor: a.ajuste, moneda: a.moneda }));
  // `porcentaje === null` ⟺ el avance se declaró por IMPORTE, y eso sólo lo
  // escribe la orden del hub: es lo que vino de Backlog. El avance manual lleva
  // porcentaje, así que la distinción no necesita una segunda consulta.
  const avanceDeBacklog = avancePendiente.filter((a) => a.porcentaje === null);
  const totalBacklog = sumar(
    avanceDeBacklog.map((a) => ({ valor: a.ajuste, moneda: a.moneda })));

  /**
   * El botón (o los botones) de un paso pendiente. La lógica es la de siempre:
   * la base dice `comprobable` y el `motivo`, y de ahí sale qué se puede hacer.
   * Un paso pendiente sin botón acá —`faltan_tipos`, `falta_declarar_avance`,
   * `falta_depreciar`— no es un olvido: su `motivo` ya dice dónde se resuelve.
   */
  function accionDe(p: PasoDelCierre) {
    if (cerrado || !escribe) return null;
    return (
      <>
        {/* ▶ Firmar solo donde la BASE dice que no puede comprobar. */}
        {!p.comprobable && (
          <span style={{ display: 'inline-flex', gap: 'var(--s3)' }}>
            <input
              className="input input--sm" placeholder={t('cierre.note')}
              value={nota[p.paso] ?? ''}
              onChange={(e) => setNota({ ...nota, [p.paso]: e.target.value })}
            />
            <button
              className="btn btn--ghost btn--sm" type="button" disabled={ocupado}
              onClick={() => void correr(() => afirmarPaso(
                pedir, anio, mes, p.paso, nota[p.paso]?.trim() || null))}
            >
              {t('cierre.sign')}
            </button>
          </span>
        )}
        {p.paso === 3 && p.estado === 'pendiente' && (
          <button
            className="btn btn--secondary btn--sm" type="button" disabled={ocupado}
            onClick={() => void correr(async () => {
              const r = await revaluarPeriodo(pedir, anio, mes);
              // ▶ «Revaluado · 0» no dice nada. Y cero es el caso NORMAL de una
              // empresa en colones: la revaluación corre, no encuentra partidas
              // y termina bien.
              setAviso(`${t('cierre.done')} · ${
                r.length === 0
                  ? t('cierre.doneNone')
                  : t('cierre.doneCount', { lineas: String(r.length) })}`);
            })}
          >
            {t('cierre.revalue')}
          </button>
        )}
        {/* ▶▶ EL 5, 7, 8 y 11 SE CORREN DESDE ACÁ, y no desde una pantalla propia
            como la depreciación. La diferencia es que éstos no tienen nada que
            administrar: la tasa y la matriz viven en Configuración y lo único
            que pasa cada mes es este botón. Una pantalla para un botón es una
            pantalla que nadie abre.

            ▶ Y sólo aparece cuando el paso está PENDIENTE por falta del asiento.
            Con la tasa sin declarar, la matriz sin confirmar o el avance sin
            declarar, el motivo ya dice qué falta y dónde — ofrecer un botón que
            va a contestar `SB237` es mandar a alguien a averiguar apretándolo. */}
        {(p.motivo === 'falta_provisionar_renta'
          || p.motivo === 'falta_provisionar_deterioro'
          || p.motivo === 'falta_provisionar_diferido'
          || p.motivo === 'falta_reconocer_avance') && (
          <button
            className="btn btn--secondary btn--sm" type="button" disabled={ocupado}
            onClick={() => void correr(async () => {
              /* ▶ El paso 5 no provisiona: reconoce. Va por su propia función y
                 devuelve la misma forma —importe y asiento— porque los tres son
                 el mismo gesto desde acá: asentar la diferencia contra un
                 objetivo. Añadir un `if` por cada uno sería convertir un gesto
                 en cuatro caminos que hay que mantener iguales. */
              const que = p.paso === 7 ? 'renta'
                : p.paso === 11 ? 'diferido' : 'deterioro';
              const r = p.paso === 5
                ? await reconocerAvance(pedir, anio, mes)
                : await provisionar(pedir, que, anio, mes);
              const importe = 'ajuste' in r ? r.ajuste : r.total;
              // El ajuste es lo que se asentó, y puede ser negativo: una cartera
              // que mejora necesita MENOS estimación.
              /* ▶ Y con SU rótulo. Decía `cierre.done`, que es «Revaluación
                 corrida»: la caminata provisionó la renta y la pantalla anunció
                 una revaluación. Una frase prestada de otro caso no se nota mal
                 — se lee como si fuera la del caso que uno mira. */
              setAviso(`${t(
                p.paso === 5 ? 'cierre.doneProgress'
                  : que === 'renta' ? 'cierre.doneTax'
                    : que === 'diferido' ? 'cierre.doneDeferred'
                      : 'cierre.doneBad')} · ${
                r.asiento === null
                  ? t('cierre.provisionNone')
                  : t('cierre.provisionOk', {
                    importe: monto({ valor: importe, moneda }),
                    asiento: String(r.asiento),
                  })}`);
            })}
          >
            {t(p.paso === 5 ? 'cierre.recogniseProgress'
              : p.paso === 7 ? 'cierre.provisionTax'
                : p.paso === 11 ? 'cierre.provisionDeferred'
                  : 'cierre.provisionBad')}
          </button>
        )}
      </>
    );
  }

  return (
    <div>
      <header className="page-head">
        <div>
          <h1>{t('cierre.title')}</h1>
          <p className="small muted">{t('cierre.sub')}</p>
        </div>
        <span className="spacer" />
        <label className="field field--inline">
          <span className="field__label">{t('cierre.period')}</span>
          <select
            className="select" value={mes}
            onChange={(e) => setMes(Number.parseInt(e.target.value, 10))}
          >
            {MESES.map((m) => <option key={m} value={m}>{String(m).padStart(2, '0')}</option>)}
          </select>
          <input
            className="input input--mono" style={{ width: '6rem' }} value={anio}
            onChange={(e) => {
              const n = Number.parseInt(e.target.value, 10);
              if (Number.isSafeInteger(n)) setAnio(n);
            }}
          />
        </label>
      </header>

      {fallo && (
        <div className="alert alert--warn" style={{ marginBottom: 'var(--s6)' }}>
          <span>{t(fallo as never)}</span>
        </div>
      )}
      {aviso && (
        <div className="alert" style={{ marginBottom: 'var(--s6)' }}>
          <span>{aviso}</span>
        </div>
      )}

      {/* ▶ El estado del mes de un vistazo: una barra y una frase. Es lo primero
          que mira quien entra — cuánto falta para cerrar, o que ya se puede. */}
      {pasos !== null && !cerrado && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s4)', marginBottom: 'var(--s5)' }}>
          <div style={{ flex: 1, height: '8px', borderRadius: '99px', background: 'var(--acc-soft)', overflow: 'hidden' }}>
            <div style={{ width: `${progresoPct}%`, height: '100%', background: 'var(--acc)' }} />
          </div>
          <span className="small" style={{ whiteSpace: 'nowrap' }}>
            {bloqueantes.length === 0
              ? t('cierre.ready')
              : t('cierre.progress', {
                n: String(bloqueantes.length), m: String(totalBloquea) })}
          </span>
        </div>
      )}

      {/* ▶▶ EL AVISO DEL AVANCE — la señal de que hay un ingreso reportado y
          listo para asentar. Es lo que un contador espera después de que el
          producto de proyectos (Backlog) cierra su mes: el avance ya está
          declarado y sólo falta reconocerlo para poder cerrar. Sale del propio
          paso 5, no de una segunda consulta. */}
      {!cerrado && hayAvance && (
        <div className="alert alert--info" style={{ marginBottom: 'var(--s6)' }}>
          <span>
            {avanceDeBacklog.length > 0 && totalBacklog
              ? t('cierre.avanceBacklog', {
                n: String(avanceDeBacklog.length),
                total: monto({ valor: totalBacklog.valor, moneda: totalBacklog.moneda }),
              })
              : t('cierre.avanceReady')}
          </span>
        </div>
      )}

      {cerrado && (
        <div className="alert alert--ok" style={{ marginBottom: 'var(--s6)' }}>
          <span>{t('cierre.closed')}</span>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          ▶▶ LOS EJERCICIOS, Y EL ACTO DE ABRIR UNO

          Hasta la 0078 los periodos se creaban UNA vez, al provisionar la
          empresa, para el año en que nacía. Nada más los volvía a crear: el 1
          de enero siguiente el producto entero se paraba con «la fecha no cae
          en ningún periodo del ejercicio» — un mensaje que culpa al dato
          cuando el que falta es un acto del sistema.

          Ahora existen once años. Y no están abiertos, porque un periodo
          abierto es un sitio donde alguien puede escribir: con once años
          abiertos, un `2031` tecleado por `2021` entra sin que nada se queje.
          Abrir el año es de una persona, y queda en la bitácora.
          ═══════════════════════════════════════════════════════════════ */}
      {/* ▶ Plegado y fuera del camino: abrir o cerrar un año es un acto de ADMIN
          poco frecuente, no el cierre mensual. Antes dominaba el centro con once
          filas; ahora es una línea que se abre si hace falta. */}
      {ejercicios && ejercicios.length > 0 && (
        <details className="card card--pad0" style={{ marginBottom: 'var(--s6)' }}>
          <summary className="small muted" style={{ cursor: 'pointer', padding: 'var(--s4) var(--s5)' }}>
            {t('cierre.exercisesManage')}
          </summary>
          <div className="card__head" style={{ borderTop: '1px solid var(--border)' }}>
            <span className="small muted">{t('cierre.exercisesWhy')}</span>
          </div>
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>{t('cierre.exerciseYear')}</th>
                  <th>{t('cierre.exerciseState')}</th>
                  <th style={{ textAlign: 'right' }}>{t('cierre.exerciseEntries')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {ejercicios.map((x) => (
                  <tr key={x.anio} className={x.anio === anio ? 'is-total' : undefined}>
                    <td className="mono">{x.anio}</td>
                    <td className="small">
                      {x.futuros === 12
                        ? <span className="chip">{t('cierre.exerciseFuture')}</span>
                        : (
                          <>
                            <span className="chip chip--ok">
                              {t('cierre.exerciseOpenN', { n: String(x.abiertos + x.revaluados) })}
                            </span>
                            {x.cerrados > 0 && (
                              <> <span className="chip">
                                {t('cierre.exerciseClosedN', { n: String(x.cerrados) })}
                              </span></>
                            )}
                            {/* Un año a medio abrir existe: se abre, se cierra
                                enero, y los once que quedan siguen en futuro
                                sólo si alguien los dejó así. Decirlo evita que
                                el botón parezca roto. */}
                            {x.futuros > 0 && (
                              <> <span className="chip chip--warn">
                                {t('cierre.exerciseFutureN', { n: String(x.futuros) })}
                              </span></>
                            )}
                          </>
                        )}
                    </td>
                    <td className="mono small" style={{ textAlign: 'right' }}>{x.asientos}</td>
                    <td style={{ textAlign: 'right' }}>
                      {x.futuros > 0 && administra && (
                        <button
                          className="btn btn--secondary btn--sm" type="button"
                          disabled={ocupado}
                          onClick={() => void correr(async () => {
                            const n = await abrirEjercicio(pedir, x.anio);
                            setAviso(t('cierre.exerciseOpened',
                              { anio: String(x.anio), n: String(n) }));
                          })}
                        >
                          {t('cierre.openExercise', { anio: String(x.anio) })}
                        </button>
                      )}

                      {/* ═════════════════════════════════════════════════════
                          ▶▶ CERRAR EL EJERCICIO — la función existía desde la
                          `0079` y NINGUNA pantalla la llamaba.
                          ═════════════════════════════════════════════════════

                          Era lo contrario del guardián 10 —una función concedida
                          sin llamador— y no la denunciaba nadie porque ese
                          guardián mira endpoints, no funciones de base que
                          todavía no tienen uno.

                          ▶ `x.cerrable` lo dice la BASE (`0092`). La pantalla no
                          lo calcula, y no por comodidad: la condición es «once
                          meses quietos y DICIEMBRE abierto», y eso no se deduce
                          de los conteos — un año con enero abierto y diciembre
                          cerrado da `abiertos = 1` y `cerrados = 11` igual que
                          uno listo. Medido: la base contesta `false` para ese
                          caso y `true` para el otro, con los mismos números.

                          ▶ Y `tiene_cierre` apaga el botón después, en vez de
                          dejarlo ofreciendo algo que la base rechaza con
                          `SB211` — un segundo asiento de cierre duplicaría el
                          resultado del año en patrimonio. */}
                      {x.tiene_cierre ? (
                        <span className="chip chip--ok">{t('cierre.yearClosed')}</span>
                      ) : x.cerrable && administra && (
                        <button
                          className="btn btn--secondary btn--sm" type="button"
                          disabled={ocupado}
                          onClick={() => void correr(async () => {
                            const n = await cerrarEjercicio(pedir, x.anio);
                            setAviso(t('cierre.yearClosedOk',
                              { anio: String(x.anio), n: String(n) }));
                          })}
                        >
                          {t('cierre.closeYear', { anio: String(x.anio) })}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}

      {pasos === null && (
        <section className="card"><p className="muted">{t('common.loading')}</p></section>
      )}

      {/* ── ▶ Lo que requiere una acción, cada uno su tarjeta ── */}
      {porHacer.length > 0 && (
        <section style={{ marginBottom: 'var(--s6)' }}>
          <h3 style={{ margin: 'var(--s5) 0 var(--s3)' }}>{t('cierre.groupAction')}</h3>
          {porHacer.map((p) => (
            <div key={p.paso} className="card" style={{ marginBottom: 'var(--s4)' }}>
              <div className="card__head">
                <h3>
                  <span className="mono small muted" style={{ marginRight: 'var(--s3)' }}>
                    {String(p.paso).padStart(2, '0')}
                  </span>
                  {t(`cierre.paso.${p.paso}` as never)}
                </h3>
                <span className="spacer" />
                {p.bloquea && (
                  <span className="chip chip--warn">{t('cierre.blocks')}</span>
                )}
              </div>
              {p.motivo && (
                <p className="small muted" style={{ margin: 'var(--s3) 0' }}>
                  {t(`cierre.motivo.${p.motivo}` as never)}
                  {/* El dato va aparte de la frase: son las fechas que faltan, y
                      sin ellas «faltan tipos» obliga a ir a buscar cuáles. */}
                  {p.dato && <span className="mono"> {p.dato}</span>}
                </p>
              )}
              {/* ▶ Lo que se va a reconocer, por proyecto. Sale del avance que ya
                  está declarado —el que Backlog reportó—, así que el contador ve
                  QUÉ asienta sin ir a Proyectos. El importe exacto del asiento lo
                  calcula `reconocer_avance`; acá se enseña el objetivo pendiente,
                  no una segunda versión del cálculo. */}
              {p.paso === 5 && avancePendiente.length > 0 && (
                <div className="table-wrap" style={{ marginTop: 'var(--s3)' }}>
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th>{t('cierre.avanceProject')}</th>
                        <th style={{ textAlign: 'right' }}>{t('cierre.avanceAmount')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {avancePendiente.map((a) => (
                        <tr key={a.proyecto}>
                          <td>
                            {a.nombre}
                            {a.porcentaje === null && (
                              <span className="chip chip--acc" style={{ marginLeft: 'var(--s3)' }}>
                                {t('cierre.fromBacklog')}
                              </span>
                            )}
                          </td>
                          <td className="mono" style={{ textAlign: 'right' }}>
                            {monto({ valor: a.ajuste, moneda: a.moneda })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {!cerrado && escribe && (
                <div className="page-actions" style={{ marginTop: 'var(--s4)' }}>
                  {accionDe(p)}
                </div>
              )}
            </div>
          ))}
        </section>
      )}

      {/* ── ▶ Lo resuelto y lo que no aplica — plegado, no estorba a la acción ── */}
      {resueltos.length > 0 && (
        <details className="card card--pad0" style={{ marginBottom: 'var(--s6)' }}>
          <summary className="small muted" style={{ cursor: 'pointer', padding: 'var(--s4) var(--s5)' }}>
            {t('cierre.groupDone')}
          </summary>
          <div className="table-wrap">
            <table className="tbl">
              <tbody>
                {resueltos.map((p) => (
                  <tr key={p.paso}>
                    <td className="mono small muted" style={{ width: '2rem' }}>
                      {String(p.paso).padStart(2, '0')}
                    </td>
                    <td>
                      {t(`cierre.paso.${p.paso}` as never)}
                      {p.motivo && (
                        <div className="small muted">
                          {t(`cierre.motivo.${p.motivo}` as never)}
                        </div>
                      )}
                    </td>
                    <td className="small" style={{ width: '9rem', textAlign: 'right' }}>
                      <span className={
                        p.estado === 'cumplido' ? 'chip chip--ok'
                          : p.estado === 'afirmado' ? 'chip chip--acc'
                            : p.estado === 'no_aplica' ? 'chip chip--soft'
                              : 'chip'
                      }
                      >
                        {p.motivo === 'al_cerrar'
                          ? t('cierre.atClose')
                          : t(`cierre.estado.${p.estado}` as never)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="card__foot">
            <span className="small muted">{t('cierre.signWhy')}</span>
          </div>
        </details>
      )}

      {escribe && (
        <div className="page-actions" style={{ marginTop: 'var(--s6)' }}>
          {!cerrado && (
            <button
              className="btn btn--primary" type="button"
              // Se deshabilita cuando quedan bloqueantes, y la base lo rechaza
              // igual con SB080. Esto solo evita el viaje.
              disabled={ocupado || pasos === null || bloqueantes.length > 0}
              onClick={() => void correr(() => cerrarPeriodo(pedir, anio, mes))}
            >
              {t('cierre.close')}
            </button>
          )}
          {cerrado && (
            <>
              <button
                className="btn btn--secondary" type="button" disabled={ocupado}
                onClick={() => void correr(() => reabrirPeriodo(pedir, anio, mes))}
              >
                {t('cierre.reopen')}
              </button>
              <span className="small muted">{t('cierre.reopenWhy')}</span>
            </>
          )}
        </div>
      )}

      {/* ── Lo que se lleva quien cerró el mes ── */}
      <section className="card" style={{ marginTop: 'var(--s6)' }}>
        <div className="card__head">
          <h3>{t('cierre.export')}</h3>
        </div>
        <p className="small muted">{t('cierre.exportWhy')}</p>

        {/* ▶ Un mes ABIERTO se exporta igual, y se dice que puede cambiar.
            Negarse sería peor: quien revisa antes de cerrar necesita
            exactamente estos archivos. Lo que no se puede es entregarlos como
            si fueran definitivos. */}
        {!cerrado && (
          <p className="small">
            <span className="alert alert--warn alert--inline">{/* ▶ Era un `chip`, y un chip no
                      envuelve: 445 px en una pantalla de 375 desplazaban la
                      página entera de lado. Un chip es un RÓTULO —«Aceptado»— y
                      esto es una frase. */}<span>{t('cierre.exportOpen')}</span></span>
          </p>
        )}

        <div className="page-actions" style={{ marginTop: 'var(--s5)' }}>
          {EXPORTABLES.map((x) => (
            <button
              key={x.tipo} className="btn btn--secondary btn--sm" type="button"
              disabled={bajando !== null}
              onClick={() => void exportar(x.tipo)}
            >
              {t(x.clave)}
            </button>
          ))}
        </div>
      </section>

      <p className="field__hint" style={{ marginTop: 'var(--s6)' }}>
        {comoFecha(hoy())}
      </p>
    </div>
  );
}
